
import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { 
  getProducts, 
  Product, 
  getCurrentUserOrganizationId,
  setOrganizationIdCache 
} from '@/lib/supabase/products';

interface UseProductFetchingProps {
  shopFilter?: string[];
  shopId?: string;
  organizationId?: string;
}

export const useProductFetching = ({ shopFilter, shopId, organizationId: propOrganizationId }: UseProductFetchingProps = {}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState<string | null>(propOrganizationId || null);
  const { toast } = useToast();
  
  // Utilisez une référence pour suivre le dernier shopId demandé
  const lastRequestedShopIdRef = useRef<string | undefined>(shopId);

  // Précharger l'ID de l'organisation s'il n'est pas fourni
  useEffect(() => {
    const preloadOrganizationId = async () => {
      try {
        if (!organizationId) {
          const orgId = await getCurrentUserOrganizationId();
          if (orgId) {
            console.log('Got organization ID from cache or function:', orgId);
            setOrganizationId(orgId);
            setOrganizationIdCache(orgId);
          }
        }
      } catch (error) {
        console.error('Failed to preload organization ID:', error);
      }
    };
    
    preloadOrganizationId();
  }, [organizationId]);

  // Fonction pour récupérer les produits
  const fetchProducts = useCallback(async () => {
    // Mettre à jour la référence du dernier shopId demandé
    lastRequestedShopIdRef.current = shopId;
    
    setLoading(true);
    
    try {
      // S'assurer que nous avons un ID d'organisation avant de récupérer
      const currentOrgId = organizationId || await getCurrentUserOrganizationId();
      if (!currentOrgId) {
        console.error('No organization ID available, cannot fetch products');
        toast({
          title: 'Erreur',
          description: "Impossible de récupérer les produits sans identifiant d'organisation",
          variant: 'destructive',
        });
        
        setProducts([]);
        setFilteredProducts([]);
        setLoading(false);
        return;
      }

      console.log('Fetching products with shopId:', shopId, 'and organizationId:', currentOrgId);
      
      // Récupérer les produits avec les filtres appropriés
      const fetchedProducts = await getProducts(shopId, currentOrgId);
      
      console.log('Fetched products:', fetchedProducts.length);
      
      // Vérifier si le shopId a changé pendant la requête
      if (lastRequestedShopIdRef.current !== shopId) {
        console.log('ShopId changed during fetch, aborting update');
        return;
      }
      
      // Mettre à jour les états en une seule opération
      setProducts(fetchedProducts);
      setFilteredProducts(fetchedProducts);
      
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer les produits',
        variant: 'destructive',
      });
      
      if (lastRequestedShopIdRef.current === shopId) {
        setProducts([]);
        setFilteredProducts([]);
      }
    } finally {
      if (lastRequestedShopIdRef.current === shopId) {
        setLoading(false);
      }
    }
  }, [toast, organizationId, shopId]); 

  // Déclencher le chargement lorsque shopId ou organizationId change
  useEffect(() => {
    console.log('Shop ID or organization ID changed, triggering fetch:', { shopId, organizationId });
    if (organizationId) {
      fetchProducts();
    }
  }, [fetchProducts, organizationId, shopId]);

  return {
    products,
    filteredProducts,
    loading,
    fetchProducts,
    organizationId
  };
};
